import { useState } from "react";
import { ChevronDown, ChevronRight, Download, FileArchive } from "lucide-react";
import { AttachedFile, getProcedureZipUrl } from "@/src/api/Tenant";
import { DownloadSection, groupFilesBySections } from "@/src/lib/downloadSections";

export function formatFileSize(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

/** Suma rozmiarów plików procedury + tekstu planu / legendy wgranych osobno
 *  (te same trafiają do ZIP-a, o ile nie są duplikatem pliku z listy). */
export function procedureTotalSize(files: AttachedFile[] | undefined, payload: any): number {
  const list = files ?? [];
  let total = list.reduce((s, f) => s + (f.fileSize ?? 0), 0);
  for (const key of ["mpzpPlanText", "mpzpLegenda"]) {
    const m = payload?.[key];
    if (!m?.fileSize) continue;
    const dup = list.some((f) => f.filename === m.filename && f.fileSize === m.fileSize);
    if (!dup) total += m.fileSize;
  }
  return total;
}

type Props = {
  tenant: string;
  procedureId: string;
  files: AttachedFile[] | undefined;
  payload: any;
  /** Lista plików domyślnie zwinięta (formularz wniosku). */
  collapsible?: boolean;
  /** Styl nagłówków sekcji (motyw strony gminy). */
  sectionTitleStyle?: React.CSSProperties;
  title?: string;
};

/**
 * Pliki do pobrania procedury: przycisk "Pobierz komplet (ZIP)" + lista
 * plików pogrupowana wg `payload.downloadSections`. Używane na stronie
 * gminy (karta procedury), na stronie procedury i w formularzu uwagi.
 */
export default function ProcedureDownloads({
  tenant,
  procedureId,
  files,
  payload,
  collapsible = false,
  sectionTitleStyle,
  title = "Dokumenty do pobrania",
}: Props) {
  const [expanded, setExpanded] = useState(!collapsible);
  const list = files ?? [];
  const hasMpzpExtra = !!payload?.mpzpPlanText || !!payload?.mpzpLegenda;
  if (list.length === 0 && !hasMpzpExtra) return null;
  const total = procedureTotalSize(list, payload);
  const groups = groupFilesBySections(list, payload?.downloadSections as DownloadSection[] | undefined);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h3 className="text-lg font-semibold" style={sectionTitleStyle}>
          {title}
        </h3>
        <a
          href={getProcedureZipUrl(tenant, procedureId)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 transition-colors"
          title="Wszystkie dokumenty projektu planu w jednym pliku ZIP">
          <FileArchive className="h-4 w-4" />
          Pobierz komplet (ZIP{total > 0 ? `, ${formatFileSize(total)}` : ""})
        </a>
      </div>
      {list.length > 0 && collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900 mb-1">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {expanded ? "Ukryj listę plików" : `Pokaż pojedyncze pliki (${list.length})`}
        </button>
      )}
      {expanded &&
        groups.map((group, idx) => (
          <div key={idx} className="mt-3 first:mt-1">
            <h4 className="text-sm font-semibold text-gray-800 mb-1.5">{group.title}</h4>
            <div className="space-y-1.5">
              {group.files.map((file) => (
                <a
                  key={file.id}
                  href={file.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded hover:border-gray-400 hover:bg-white text-sm group transition-colors">
                  <Download className="h-4 w-4 text-gray-400 shrink-0 group-hover:text-gray-600" />
                  <span className="truncate flex-1 text-gray-700 group-hover:text-gray-900">{file.filename}</span>
                  {file.fileSize ? (
                    <span className="text-xs text-gray-400 shrink-0">{formatFileSize(file.fileSize)}</span>
                  ) : null}
                </a>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}
